import React, { useEffect, useRef, useState } from "react";

interface Props {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
}

interface TabProps {
  title: string;
  selected: boolean;
  onClick: () => void;
}

function DeploymentTab(props: TabProps) {
  return (
    <div
      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 cursor-pointer ${
        props.selected
          ? "border-cornflower-blue-400 text-cornflower-blue-400"
          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
      onClick={props.onClick}
    >
      {props.title}
    </div>
  );
}

export default function DeploymentTabs(props: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [stickyOffset, setStickyOffset] = useState(0);

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;

    const calculateOffset = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const parent = container.parentElement;
      const stickyHeader = parent?.previousElementSibling as HTMLElement | null;

      if (!stickyHeader) {
        setStickyOffset(0);
        return;
      }

      const styles = window.getComputedStyle(stickyHeader);
      const marginBottom = parseFloat(styles.marginBottom || "0");
      const nextOffset = Math.ceil(stickyHeader.getBoundingClientRect().height + marginBottom);

      setStickyOffset((prev) => (prev !== nextOffset ? nextOffset : prev));
    };

    const handleScroll = () => {
      window.requestAnimationFrame(calculateOffset);
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);
    window.addEventListener("scroll", handleScroll, true);

    const container = containerRef.current;
    const parent = container?.parentElement;
    const stickyHeader = parent?.previousElementSibling as HTMLElement | null;

    if (stickyHeader && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => calculateOffset());
      resizeObserver.observe(stickyHeader);
    }

    return () => {
      window.removeEventListener("resize", calculateOffset);
      window.removeEventListener("scroll", handleScroll, true);
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="top-9 sticky z-40 w-full bg-slate-gray-50"
      style={{ top: stickyOffset ? `${stickyOffset}px` : undefined }}
    >
      <div className="flex justify-center border-b-2 border-slate-200 w-full">
        <DeploymentTab
          title="General"
          selected={props.selectedTab === "General"}
          onClick={() => props.setSelectedTab("General")}
        />
        <DeploymentTab
          title="Deployment Code"
          selected={props.selectedTab === "Deployment Code"}
          onClick={() => props.setSelectedTab("Deployment Code")}
        />
      </div>
      <div className="h-4 bg-slate-gray-50" aria-hidden="true" />
    </div>
  );
}